/**
 * SoleadLab 2.0 — IV Reporting API Client
 */

import type {
  IVSession,
  IVMeasurement,
  IVFinding,
  IVReport,
  CreateSessionInput,
  UpdateSessionInput,
  UpdateMeasurementInput,
  ImportPayload,
  ImportResult,
  AnalysisResult,
  GenerateReportResult,
} from '@/types/iv';
import { ApiError, apiFetch, apiFetchRaw } from './apiFetch';

// ─── Session endpoints ────────────────────────────────────────────────────────

export async function createSession(input: CreateSessionInput, siteId: string): Promise<IVSession> {
  return apiFetch<IVSession>(`/api/v2/sites/${siteId}/iv/sessions`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function listSessions(siteId: string): Promise<IVSession[]> {
  return apiFetch<IVSession[]>(`/api/v2/sites/${siteId}/iv/sessions`);
}

export async function getSession(id: string): Promise<IVSession> {
  return apiFetch<IVSession>(`/api/v2/iv/sessions/${id}`);
}

export async function updateSession(id: string, input: UpdateSessionInput): Promise<IVSession> {
  return apiFetch<IVSession>(`/api/v2/iv/sessions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function deleteSession(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/v2/iv/sessions/${id}`, { method: 'DELETE' });
}

export async function uploadSessionSourceFile(
  sessionId: string,
  file: File,
): Promise<{ r2_key: string; file_name: string }> {
  return apiFetch<{ r2_key: string; file_name: string }>(
    `/api/v2/iv/sessions/${sessionId}/source-file`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'X-File-Name': file.name,
      },
      body: file,
    },
    60_000,
  );
}

export async function downloadSourceFile(sessionId: string): Promise<Blob> {
  const response = await apiFetchRaw(`/api/v2/iv/sessions/${sessionId}/source-file`);
  if (!response.ok) throw new ApiError(`HTTP ${response.status}`, response.status, 'DOWNLOAD_ERROR');
  return response.blob();
}

export async function listImports(sessionId: string): Promise<import('@/types/iv').IVColumnMapping[]> {
  return apiFetch<import('@/types/iv').IVColumnMapping[]>(`/api/v2/iv/sessions/${sessionId}/imports`);
}

// ─── Measurement endpoints ────────────────────────────────────────────────────

export async function importMeasurements(sessionId: string, payload: ImportPayload): Promise<ImportResult> {
  return apiFetch<ImportResult>(`/api/v2/iv/sessions/${sessionId}/import`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }, 30_000);
}

export async function listMeasurements(sessionId: string): Promise<IVMeasurement[]> {
  return apiFetch<IVMeasurement[]>(`/api/v2/iv/sessions/${sessionId}/measurements`);
}

export async function updateMeasurement(id: string, input: UpdateMeasurementInput): Promise<IVMeasurement> {
  return apiFetch<IVMeasurement>(`/api/v2/iv/measurements/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function deleteMeasurement(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/v2/iv/measurements/${id}`, { method: 'DELETE' });
}

export async function bulkUpdateMeasurements(
  sessionId: string,
  ids: string[],
  updates: {
    label?: string | null;
    marker2?: string | null;
    marker3?: string | null;
    map_label?: string | null;
    /** F-01D-2: layout this batch's measurements should be assigned against. */
    layout_id?: string | null;
  },
): Promise<void> {
  await apiFetch<unknown>(`/api/v2/iv/sessions/${sessionId}/measurements/bulk`, {
    method: 'PATCH',
    body: JSON.stringify({ ids, ...updates }),
  });
}

/**
 * Assign map labels to measurements in bulk.
 *
 * F-01D-2: each assignment may now carry a `layout_id` alongside `map_label`.
 * The batcher groups by the composite `(layout_id, map_label)` key so a
 * single bulk-update PATCH lands per coherent (layout, label) pair — every
 * row in the PATCH body has the same two values set together. Legacy
 * callers passing only `map_label` still work; the grouping key collapses
 * to `(null, label)` and the legacy PATCH shape is preserved.
 *
 * Clearing semantics: passing `{ map_label: null, layout_id: null }` is the
 * canonical "unassign" pair — both columns are nulled out together.
 */
export async function bulkAssignMapLabels(
  sessionId: string,
  assignments: Array<{ id: string; map_label: string | null; layout_id?: string | null }>,
): Promise<void> {
  // Group by composite (layout_id, map_label) so each PATCH body assigns one
  // coherent target. `' '` (NUL) is used as a separator that cannot
  // appear in either field's value.
  const compositeKey = (layoutId: string | null | undefined, mapLabel: string | null): string =>
    `${layoutId ?? ''} ${mapLabel ?? ''}`;

  const groups = new Map<string, {
    map_label: string | null;
    layout_id: string | null | undefined;
    ids: string[];
  }>();
  for (const { id, map_label, layout_id } of assignments) {
    const key = compositeKey(layout_id, map_label);
    let bucket = groups.get(key);
    if (!bucket) {
      bucket = { map_label, layout_id, ids: [] };
      groups.set(key, bucket);
    }
    bucket.ids.push(id);
  }

  const BATCH = 200;
  const calls: Promise<void>[] = [];
  for (const { map_label, layout_id, ids } of groups.values()) {
    for (let i = 0; i < ids.length; i += BATCH) {
      const chunk = ids.slice(i, i + BATCH);
      // Only include layout_id in the body when the caller explicitly passed
      // it (including null for clears). Legacy callers that don't pass it at
      // all keep their pre-F-01D-2 wire shape.
      const updates: { map_label: string | null; layout_id?: string | null } = { map_label };
      if (layout_id !== undefined) updates.layout_id = layout_id;
      calls.push(bulkUpdateMeasurements(sessionId, chunk, updates));
    }
  }
  await Promise.all(calls);
}

// ─── Analysis endpoints ───────────────────────────────────────────────────────

export async function analyzeSession(sessionId: string): Promise<AnalysisResult> {
  return apiFetch<AnalysisResult>(`/api/v2/iv/sessions/${sessionId}/analyze`, {
    method: 'POST',
  }, 30_000);
}

export async function listFindings(sessionId: string): Promise<IVFinding[]> {
  return apiFetch<IVFinding[]>(`/api/v2/iv/sessions/${sessionId}/findings`);
}

// ─── Report endpoints ─────────────────────────────────────────────────────────

export async function generateReport(sessionId: string): Promise<GenerateReportResult> {
  return apiFetch<GenerateReportResult>(`/api/v2/iv/sessions/${sessionId}/report`, {
    method: 'POST',
  }, 30_000);
}

export async function listReports(sessionId: string): Promise<IVReport[]> {
  return apiFetch<IVReport[]>(`/api/v2/iv/sessions/${sessionId}/reports`);
}

export async function getReport(reportId: string): Promise<IVReport> {
  return apiFetch<IVReport>(`/api/v2/iv/reports/${reportId}`);
}
